import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSaidaSuave } from '../hooks/useSaidaSuave';
import { avisar } from './aviso';

// Cor do marcador por urgência do item (critico/atencao/info) — não é
// mais o que decide onde o item aparece (isso agora é por origem:
// minhas/equipe/gestaoSala), só o acento visual de prioridade.
/* `COR_URGENCIA` saiu em 28/08: a urgência virou faixa na borda esquerda
   da linha (`.n-row.crit` / `.n-row.aten`), e cor de estado em `style`
   inline é o defeito que o portão 32 persegue. */

/**
 * Generalizado (10/08/2026, "padronizar tudo") pra funcionar fora do
 * SGQ — antes chamava sempre /notificacoes e navegava sempre pra
 * /sala/X, os dois fixos e específicos do SGQ (tabelas pa_planos/
 * nc_registros/etc., numeração de sala do SGQ). RH e Geral têm dado
 * diferente (ou nenhum ainda) — em vez de duplicar o dropdown inteiro,
 * dois props opcionais resolvem: `carregar` (função que devolve uma
 * Promise no mesmo formato {minhas,equipe,gestaoSala,total}) e
 * `aoClicar` (função que decide a navegação por item). Sem os dois
 * props, comportamento idêntico a antes — SGQ (<Notificacoes />, sem
 * props) não precisou mudar nada.
 */
/* ⚠ ÍCONE POR ORIGEM (28/08/2026, demo aprovada). Distingue de relance
   uma calibração vencida de uma expedição esperando aprovação — hoje as
   duas linhas são idênticas.

   Mapeado por SALA e não por tipo de pendência: a sala é o dado que toda
   pendência já traz, e é o destino do clique. Sala sem desenho próprio
   cai no genérico, que é melhor que ícone errado. */
const ICONES_SALA = {
  1:  '<path d="M21 8V5l-9-4-9 4v14l9 4 9-4"/><path d="M3 5l9 4 9-4M12 9v12"/>',
  32: '<path d="M6.5 2.5v6M12 2.5v6M17.5 2.5v6"/><path d="M4.5 5h4M10 6.5h4M15.5 4h4"/><path d="M5 20.5v-3a7 7 0 0 1 14 0v3"/><path d="M4 17.5h3v4H4zM17 17.5h3v4h-3z"/>', // Configuração de AT (26/09/2026)
  37: '<path d="M3 3v18h18"/><path d="M7 15l4-5 3 3 5-7"/>',
  19: '<rect x="3" y="4.5" width="18" height="16" rx="2"/><path d="M3 9.5h18M8 2.5v4M16 2.5v4"/>',
};
const ICONE_GENERICO = '<circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/>';

export const ICONE_ORIGEM = (sala) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    dangerouslySetInnerHTML={{ __html: ICONES_SALA[sala] || ICONE_GENERICO }} />
);

export default function Notificacoes({ carregar: carregarProp, aoClicar: aoClicarProp }) {
  const { API, isMaster } = useAuth();

  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);

  /* ⚠ DEPOIS DE `aberto`, E ISSO NÃO É DETALHE (corrigido em 28/08/2026,
     tela branca em todas as telas).

     Eu inseri este bloco ACIMA da declaração de `aberto`, e ele lê
     `aberto` no efeito e no array de dependências. `const` não sobe: ler
     antes da linha onde é declarado dá `Cannot access 'c' before
     initialization`.

     E como o sino vive na topbar, o erro derrubava QUALQUER tela — o
     relato começou como "o painel administrativo está branco", e era o
     sistema inteiro.

     ⚠ O TESTE NÃO É PARA TODO MUNDO (28/08/2026, Juan: "às vezes quem nem
     tem a necessidade vai ficar testando a função avulsa").

     A regra sai sozinha do problema: quem não está em nenhuma das três
     listas da Sala 42 não recebe push nenhum, então não tem o que testar
     — o botão seria um brinquedo. O Master sempre vê, porque é quem
     diagnostica quando alguém reclama.

     Consulta só na PRIMEIRA abertura do sino: é dado que não muda dentro
     de uma sessão, e perguntar a cada toque seria uma consulta por
     clique. */
  const [temPapelPush, setTemPapelPush] = useState(null);
  useEffect(() => {
    if (!aberto || temPapelPush !== null) return;
    API.get('/notificacoes/push/meu-estado')
      .then(r => setTemPapelPush((r.data.papeis || []).length > 0))
      .catch(() => setTemPapelPush(false));
  }, [aberto, temPapelPush, API]);
  const podeTestar = isMaster || temPapelPush === true;
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);
  const { montado, fechando } = useSaidaSuave(aberto, 180, () => setAberto(false));
  const [dados, setDados] = useState({ minhas: [], equipe: [], gestaoSala: [], total: 0 });
  const [carregando, setCarregando] = useState(false);
  const ref = useRef(null);

  const carregar = () => {
    setCarregando(true);
    const promessa = carregarProp ? carregarProp() : API.get('/notificacoes').then(r => r.data);
    promessa
      .then(d => setDados({ minhas: [], equipe: [], gestaoSala: [], total: 0, ...d }))
      .catch(e => console.error('[Notificacoes]', e.message))
      .finally(() => setCarregando(false));
  };

  useEffect(() => {
    carregar();
    const interval = setInterval(carregar, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ⚠ NAVEGA PRIMEIRO, FECHA DEPOIS (28/08/2026, relato do Juan: "ao
     clicar em uma demanda no sino ele não redireciona; funciona só pela
     lista da home").

     O `setAberto(false)` vinha antes do `navigate`. O painel usa
     `useSaidaSuave`, que desmonta em 180ms — e a desmontagem derrubava o
     clique antes de a navegação sair. A lista da home não tinha o
     problema porque não fecha nada: ela já está na tela final.

     Também caía quando `item.sala` vinha vazio: o clique não fazia nada
     e não dizia por quê. Agora avisa. */
  const irPara = (item) => {
    if (aoClicarProp) { setAberto(false); aoClicarProp(item); return; }
    if (!item.sala) {
      avisar('Esta pendência não aponta para uma sala. Avise quem cuida do sistema.');
      return;
    }
    navigate(`/sala/${item.sala}`);
    setAberto(false);
  };

  // Achata as três origens numa lista só, pro espaço compacto do dropdown —
  // itens da equipe ganham o nome da pessoa no título, pra não perder o
  // contexto de quem é responsável fora da seção dedicada (essa só existe
  // no painel grande, aberto pelos cards da Home).
  const lista = [
    ...dados.minhas,
    ...dados.equipe.flatMap(p => p.itens.map(item => ({ ...item, titulo: `${p.nome} — ${item.titulo}` }))),
    ...dados.gestaoSala,
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="t-btn" onClick={() => { setAberto(a => !a); if (!aberto) carregar(); }}
        title="O que precisa de você"
        aria-label={dados.total
          ? `O que precisa de você — ${dados.total} pendência${dados.total > 1 ? 's' : ''}`
          : 'O que precisa de você'}>
        {/* ⚠ A FORMA FECHA (28/08/2026, demo aprovada). O desenho antigo
            terminava a cúpula com um traço reto solto na base, e o
            resultado a 14px era um contorno que não fechava. Este fecha a
            silhueta e usa 1,7, o traço da casa.

            16px em vez de 14: o sino carrega a contagem por cima, e a 14
            o número encostava na borda. */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18.5 9.5a6.5 6.5 0 10-13 0c0 4.2-1.2 6-2 6.9a.8.8 0 00.6 1.3h15.8a.8.8 0 00.6-1.3c-.8-.9-2-2.7-2-6.9z" />
          <path d="M14 20.5a2.2 2.2 0 01-4 0" />
        </svg>
        {/* Contagem no lugar do ponto: o ponto dizia que existe algo, o
            número diz quanto — e some no zero. */}
        {dados.total > 0 && <span className="n-cont">{dados.total > 9 ? '9+' : dados.total}</span>}
      </button>

      {montado && (
        <div className={`n-panel ${fechando ? 'closing' : ''}`}>
          <div className="n-ph">
            O que precisa de você
            <span className="n-count">
              {dados.total === 1 ? '1 pendência' : `${dados.total} pendências`}
            </span>
          </div>
          <div className="n-lista">
            {/* Fora do ternário: dentro dele cabe um elemento só, e o
                aviso do teste tem que aparecer com a lista cheia ou vazia. */}
            {teste && (
              <p className={`n-teste ${teste.ok ? 'ok' : 'ruim'}`}>{teste.texto}</p>
            )}
            {lista.length === 0 ? (
              <div className="n-vazio">
                {carregando ? 'Carregando…' : (
                  <>
                    <b>Nada esperando você</b>
                    Quando alguma coisa precisar da sua decisão, ela aparece aqui.
                  </>
                )}
              </div>
            ) : (
              /* ⚠ AGRUPA POR URGÊNCIA (28/08/2026, demo aprovada). Hoje uma
                 calibração vencida e uma expedição esperando aprovação têm
                 exatamente o mesmo peso: mesma linha, mesmo ponto de 8px.

                 "Agora" reúne o que já passou do prazo; o resto espera. O
                 grupo só aparece quando tem item — cabeçalho de seção vazia
                 é ruído. */
              ['critico', 'outros'].map(faixa => {
                const doGrupo = faixa === 'critico'
                  ? lista.filter(x => x.urgencia === 'critico')
                  : lista.filter(x => x.urgencia !== 'critico');
                if (!doGrupo.length) return null;
                return (
                  <React.Fragment key={faixa}>
                    <div className="n-grupo">{faixa === 'critico' ? 'Agora' : 'Esperando você'}</div>
                    {doGrupo.map((item, i) => (
                      <div key={i} className={`n-row ${item.urgencia === 'critico' ? 'crit' : 'aten'}`}
                        role="button" tabIndex={0}
                        onClick={() => irPara(item)}
                        onKeyDown={(e) => { if (e.key === 'Enter') irPara(item); }}>
                        <span className="n-ic" aria-hidden="true">
                          {ICONE_ORIGEM(item.sala)}
                        </span>
                        <div className="n-txt">
                          <strong>{item.titulo}</strong>
                          {item.descricao && <div className="n-time">{item.descricao}</div>}
                        </div>
                        <span className="n-seta" aria-hidden="true">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 6l6 6-6 6" />
                          </svg>
                        </span>
                      </div>
                    ))}
                  </React.Fragment>
                );
              })
            )}
            {/* ⚠ O TESTE DESCEU PARA O RODAPÉ (28/08/2026). Na demo ele já
                estava aqui; ao implementar eu o deixei no cabeçalho, e o
                print do Juan mostrou o efeito: título, contagem e botão
                disputando uma linha só, tudo espremido.

                O rodapé aparece sempre — com lista cheia ele explica a
                regra, com lista vazia sobra o teste, que é justamente
                quando alguém desconfia do push. */}
            <div className="n-rodape">
              <span>
                {lista.length > 0
                  ? 'As pendências somem quando a atividade é feita.'
                  : 'Você será avisado no celular quando algo precisar de você.'}
              </span>
              {podeTestar && (
              <button type="button" className="n-testar"
                disabled={testando}
                title="Manda uma notificação de teste para este aparelho"
                onClick={async (e) => {
                  e.stopPropagation();
                  setTestando(true); setTeste(null);
                  try {
                    const r = await API.post('/notificacoes/push/testar');
                    /* ⚠ UMA LINHA, NÃO UM PARÁGRAFO (28/08/2026, print do
                       Juan). O texto anterior tinha três linhas de prosa e
                       ocupava mais espaço que a própria lista. O detalhe
                       longo ("se não aparecer, está bloqueado no aparelho")
                       só interessa quando o push NÃO chega — e aí a pessoa
                       já sabe, porque nada apareceu. */
                    setTeste({ ok: true, texto: `Enviado para ${r.data.enviados} aparelho(s).` });
                  } catch (err) {
                    setTeste({ ok: false, texto: err.response?.data?.erro
                      || err.response?.data?.detalhe
                      || 'Não foi possível testar agora.' });
                  } finally { setTestando(false); }
                }}>
                {testando ? 'enviando…' : 'testar push'}
              </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
